import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const errMsg = (e) => e?.response?.data?.detail || "Gagal memproses.";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "");
const STATUS_LABEL = { visible: "Tampil", hidden: "Disembunyikan", deleted: "Dihapus" };
const REASON_LABEL = { spam: "Spam", kasar: "Kasar", informasi_salah: "Info menyesatkan", lainnya: "Lainnya" };
const VIEWS = [
  { v: "comments", label: "Tanggapan" },
  { v: "reports", label: "Laporan pembaca" },
  { v: "settings", label: "Pengaturan artikel" },
];

function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-[#F3F1ED]" data-testid={testid}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} data-testid={`${testid}-${o.v}`}
          className={"px-3 py-1.5 rounded-full text-xs font-semibold transition-colors " +
            (value === o.v ? "bg-[#0F1F3D] text-white" : "text-[#4B5563] hover:text-[#0F1F3D]")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CommentsView() {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    const params = status === "reported" ? { reported: true } : { status };
    api.get("/admin/article-comments", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(load, [load]);
  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Hapus tanggapan ini dari tampilan publik?")) return;
    try {
      await api.post(`/admin/article-comments/${id}/moderate`, { action });
      toast.success("Tanggapan diperbarui");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <Segmented value={status} onChange={setStatus} testid="adc-filter" options={[
        { v: "all", label: "Semua" }, { v: "visible", label: "Tampil" }, { v: "hidden", label: "Disembunyikan" },
        { v: "deleted", label: "Dihapus" }, { v: "reported", label: "Pernah dilaporkan" },
      ]} />
      {rows.length === 0 && <div className="text-sm text-[#4B5563] p-6 bg-white rounded-2xl border border-[#EAE7E0]" data-testid="adc-empty">Belum ada tanggapan.</div>}
      {rows.map((c) => (
        <div key={c.comment_id} className="bg-white border border-[#EAE7E0] rounded-2xl p-4" data-testid={`adc-row-${c.comment_id}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#4B5563]">
            <a href={`/jurnal/${c.post_slug}`} target="_blank" rel="noreferrer" className="font-semibold text-[#1E3A8A] hover:underline">{c.post_title}</a>
            <span>· {c.user_name}</span><span>· {fmt(c.created_at)}</span>
            {c.parent_id && <span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E3A8A]">Balasan</span>}
            <span className="px-2 py-0.5 rounded-full bg-[#F3F1ED]" data-testid={`adc-status-${c.comment_id}`}>{STATUS_LABEL[c.status]}</span>
            {c.report_count > 0 && <span className="px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B]">{c.report_count} laporan</span>}
          </div>
          <p className="mt-2 text-sm text-[#1F2937] whitespace-pre-line break-words">{c.body}</p>
          <div className="mt-3 flex gap-2">
            {c.status === "visible" && <Button size="sm" variant="outline" onClick={() => act(c.comment_id, "hide")} data-testid={`adc-hide-${c.comment_id}`}>Sembunyikan</Button>}
            {c.status === "hidden" && <Button size="sm" variant="outline" onClick={() => act(c.comment_id, "unhide")} data-testid={`adc-unhide-${c.comment_id}`}>Pulihkan</Button>}
            {c.status !== "deleted" && <Button size="sm" variant="ghost" className="text-[#B91C1C]" onClick={() => act(c.comment_id, "delete")} data-testid={`adc-delete-${c.comment_id}`}>Hapus</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsView() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/article-comment-reports", { params: { status } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(load, [load]);
  const run = async (fn) => { try { await fn(); toast.success("Diperbarui"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
      <Segmented value={status} onChange={setStatus} testid="adr-filter" options={[
        { v: "open", label: "Perlu ditinjau" }, { v: "resolved", label: "Selesai" }, { v: "dismissed", label: "Diabaikan" },
      ]} />
      {rows.length === 0 && <div className="text-sm text-[#4B5563] p-6 bg-white rounded-2xl border border-[#EAE7E0]" data-testid="adr-empty">Tidak ada laporan.</div>}
      {rows.map((r) => (
        <div key={r.report_id} className="bg-white border border-[#EAE7E0] rounded-2xl p-4" data-testid={`adr-row-${r.report_id}`}>
          <div className="flex flex-wrap gap-2 text-xs text-[#4B5563]">
            <span className="px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] font-semibold">{REASON_LABEL[r.reason] || r.reason}</span>
            <span>Pelapor: {r.reporter_name}</span><span>· {fmt(r.created_at)}</span>
            <a href={`/jurnal/${r.post_slug}`} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:underline">{r.post_title}</a>
            <span className="px-2 py-0.5 rounded-full bg-[#F3F1ED]">Tanggapan: {STATUS_LABEL[r.comment_status] || "—"}</span>
          </div>
          {r.detail && <p className="mt-2 text-xs text-[#4B5563] italic">“{r.detail}”</p>}
          <p className="mt-2 text-sm text-[#1F2937] whitespace-pre-line border-l-2 border-[#EAE7E0] pl-3">{r.comment_author}: {r.comment_body}</p>
          {r.status === "open" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {r.comment_status === "visible" && (
                <Button size="sm" variant="outline" data-testid={`adr-hide-${r.report_id}`} onClick={() => run(async () => {
                  await api.post(`/admin/article-comments/${r.comment_id}/moderate`, { action: "hide" });
                  await api.patch(`/admin/article-comment-reports/${r.report_id}`, { status: "resolved" });
                })}>Sembunyikan & selesaikan</Button>
              )}
              <Button size="sm" variant="outline" data-testid={`adr-resolve-${r.report_id}`} onClick={() => run(() => api.patch(`/admin/article-comment-reports/${r.report_id}`, { status: "resolved" }))}>Tandai selesai</Button>
              <Button size="sm" variant="ghost" data-testid={`adr-dismiss-${r.report_id}`} onClick={() => run(() => api.patch(`/admin/article-comment-reports/${r.report_id}`, { status: "dismissed" }))}>Abaikan</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsView() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/article-discussions").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(load, [load]);
  const save = async (postId, patch) => {
    try {
      await api.patch(`/admin/blog/${postId}/discussion`, patch);
      toast.success("Pengaturan diskusi disimpan");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl divide-y divide-[#EAE7E0]" data-testid="ads-list">
      {rows.map((p) => (
        <div key={p.post_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between" data-testid={`ads-row-${p.post_id}`}>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#1F2937] truncate">{p.title}</div>
            <div className="text-xs text-[#4B5563]">{p.status === "published" ? "Terbit" : "Draf"} · {p.visible_count} tanggapan tampil{p.open_reports > 0 ? ` · ${p.open_reports} laporan terbuka` : ""}</div>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <label className="flex items-center gap-2 text-xs text-[#4B5563]">
              <Switch checked={p.discussion_enabled} onCheckedChange={(v) => save(p.post_id, { enabled: v })} data-testid={`ads-enable-${p.post_id}`} />
              {p.discussion_enabled ? "Aktif" : "Nonaktif"}
            </label>
            {p.discussion_enabled && (
              <Button size="sm" variant="outline" data-testid={`ads-toggle-status-${p.post_id}`}
                onClick={() => save(p.post_id, { status: p.discussion_status === "closed" ? "open" : "closed" })}>
                {p.discussion_status === "closed" ? "Buka kembali" : "Tutup diskusi"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ArticleDiscussionsTab() {
  const [view, setView] = useState("comments");
  return (
    <div className="space-y-5" data-testid="article-discussions-admin">
      <div>
        <h2 className="font-serif-display text-2xl text-[#1F2937]">Diskusi Artikel</h2>
        <p className="text-sm text-[#4B5563] mt-1">Tanggapan langsung tampil setelah dikirim. Moderasi dilakukan setelah publikasi. Diskusi default nonaktif per artikel.</p>
      </div>
      <Segmented value={view} onChange={setView} options={VIEWS} testid="adm-view" />
      {view === "comments" && <CommentsView />}
      {view === "reports" && <ReportsView />}
      {view === "settings" && <SettingsView />}
    </div>
  );
}
